import React from 'react'
import classNames from 'classnames'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { UP_NEXT_CREDITS_EARLY_BUFFER_SECONDS } from '@/constants/video'
import type { UpNextConfig } from '@/organisms/AngelPlayer/BitmovinPlayer/types'
import { useTranslate } from '@/utils/translate/translate-client'
import { useHlsPlayerUI } from './HlsPlayerUIContext'

interface HlsUpNextOverlayProps {
  upNext: UpNextConfig
  controlsVisible: boolean
}

/**
 * Next Episode button for the HLS.js player.
 *
 * Gray pill with a play icon and "Next Episode" text.
 * Visible when the control bar is showing OR after credits roll.
 * Hidden during the intro segment. Navigates to next episode on click.
 */
export const HlsUpNextOverlay: React.FC<HlsUpNextOverlayProps> = ({ upNext, controlsVisible }) => {
  const { currentTime, controller } = useHlsPlayerUI()
  const { t } = useTranslate('common')

  const { goToNext, showTime, introStartTime, introEndTime } = upNext

  if (!goToNext || showTime == null) return null

  const isInIntro =
    introStartTime != null && introEndTime != null && currentTime >= introStartTime && currentTime <= introEndTime

  const isInCreditsTimeframe = currentTime >= showTime - UP_NEXT_CREDITS_EARLY_BUFFER_SECONDS

  // The button is visible whenever controls are showing (not just during credits) because
  // our credits metadata is unreliable — many episodes have missing or inaccurate showTime values.
  // If credits metadata improves in the future, consider restricting to `isInCreditsTimeframe` only.
  // Before changing this, verify that credits timestamps are consistently populated and accurate
  // across all series and movies by auditing the ContentEpisode API responses.
  const shouldShow = (controlsVisible || isInCreditsTimeframe) && !isInIntro

  if (!shouldShow) return null

  const handleClick = () => {
    controller?.pause()
    goToNext()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={classNames(
        'absolute z-40 animate-fadein overflow-hidden rounded-lg border-0 outline-none',
        'right-1 top-1',
        'sm:bottom-[5em] sm:right-[1.25em] sm:top-auto',
      )}
    >
      <div className="flex flex-row items-center bg-core-gray-200 px-3 py-2 transition-colors hover:bg-white sm:px-4 sm:py-4">
        <PlayIcon size={18} color="core-gray-950" className="sm:hidden" />
        <PlayIcon size={24} color="core-gray-950" className="hidden sm:block" />
        <span className="photon-label-sm sm:photon-label-lg ml-1 text-core-gray-950 sm:ml-3">
          {t('nextEpisode', 'Next Episode')}
        </span>
      </div>
    </button>
  )
}
